/**
 * Service-worker update toast.
 *
 * Background: the PWA registers a workbox service worker with
 * `skipWaiting` + `clientsClaim` so a freshly deployed bundle takes
 * control of open tabs on the next navigation. That handles the
 * passive case, but a tab left open with active work in flight should
 * not silently swap the bundle underneath the user mid-task — the
 * router state, slide-over forms, and in-flight mutations would all
 * reset without warning. So the component renders a small bottom-of-
 * shell banner the moment the SW detects a new version and lets Peter
 * pick the moment to reload.
 *
 * The `onRegisteredSW` callback also wires a 10-minute poll so a tab
 * left open all day eventually notices a fresh deploy without
 * needing a navigation event to trigger the check.
 *
 * Reference: UAT 50 follow-up to spec section 7.8 (PWA).
 */

import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * In-app toast shown only while the service worker has an update
 * waiting to activate. Mounted at the shell so it survives route
 * changes; renders `null` (no DOM) while idle.
 */
export function ServiceWorkerUpdate() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      // Poll for updates every 10 minutes so a tab left open eventually
      // notices a fresh deploy without Peter triggering a navigation.
      // 10 minutes is small enough to feel responsive after a deploy
      // and large enough to not register as background network noise.
      if (registration) {
        setInterval(() => registration.update(), 10 * 60 * 1000);
      }
    },
  });

  // Idle state: no DOM at all so the toast does not occupy paint or
  // accessibility-tree weight when there is nothing to announce.
  if (!needRefresh) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-mx-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-mx-3 border border-mx-grey-800 bg-mx-card px-mx-4 py-mx-3 text-mx-body-sm text-mx-grey-300 shadow-none"
    >
      <span>A new version is ready.</span>
      <button
        type="button"
        onClick={() => updateServiceWorker(true)}
        className="mono-label text-mx-orange hover:text-mx-orange-hover"
      >
        RELOAD
      </button>
      <button
        type="button"
        onClick={() => setNeedRefresh(false)}
        aria-label="Dismiss"
        className="text-mx-grey-mid hover:text-mx-grey-300"
      >
        ×
      </button>
    </div>
  );
}
